import { Grid } from '../../../../../Common/Grid'
import { Button, Input, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { useNotification } from '../../../../../../../hooks'
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePostRequest,
  makeCoreDeleteRequest
} from "../../../../../../../utils/api"
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Paper, CircularProgress } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import React, { useMemo, useState } from "react"
import { URLS } from '../../../../constants'
import { AsyncAutocomplete } from "../../../../../../core-ui/AsyncAutocomplete"
import { AsyncAutocompleteOption } from "../../../../../../core-ui/AsyncAutocomplete/AsyncAutocompleteDefinitions"
import { DSSupportTypeDTO } from "../PackageSelection/PackageSelection";

interface DSReceptionSupportDTO {
  id: number | null
  supportNumber: number
  dsSupportTypeId: number
}

type SaveCtx = {
  prev: DSReceptionSupportDTO[] | undefined
  rowId: number | null
}

const PackageConfirmation = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const { id: orderId } = useParams<{ id: string }>()
  const [sp] = useSearchParams()
  const supplierCode = sp.get('supplierCode')
  const queryClient = useQueryClient()

  const [newSupportType, setNewSupportType] = useState<AsyncAutocompleteOption | null>(null)
  const [newSupport, setNewSupport] = useState<{
    dsSupportTypeId: number | null
    supportNumber: number
  }>({ dsSupportTypeId: null, supportNumber: 0, })

  const [isAdding, setIsAdding] = useState(false)
  const [editedQty, setEditedQty] = useState<Record<number, number>>({})
  const [savingRow, setSavingRow] = useState<Record<number, boolean>>({})

  const { data: supports, isLoading } = useQuery<DSReceptionSupportDTO[]>(['dsSupportList', orderId], async () => {
    return await makeCoreGetRequest(`/shop-reception-mobile/ds-order/ds-support-traceability/list/${orderId}/${supplierCode}`, null)
  }, {
    onError: (err) =>
      showNotification({
        message: `Eroare la preluarea suporturilor: ${formatErrorMessage(err)}`,
        type: 'error'
      }),
  })

  const { data: supportTypes, isLoading: isLoadingTypes } = useQuery<DSSupportTypeDTO[]>(
    ['dsSupportTypeList'],
    async () => await makeCoreGetRequest(`/shop-reception-mobile/ds-order/ds-support-type/list`, null),
    {
      retry: 1,
      onError: () => {
        showNotification({
          message: 'Nu s-au putut încărca tipurile de ambalaje.',
          type: 'warning',
        })
      },
    }
  )

  const saveMutation = useMutation<void, unknown, DSReceptionSupportDTO, SaveCtx>({
    mutationFn: (payload) =>
      makeCorePostRequest(`/shop-reception-mobile/ds-order/ds-support-traceability/add-support/${orderId}/${supplierCode}`, payload),

    onMutate: async (payload) => {
      const rowId = payload.id ?? null

      await queryClient.cancelQueries(['dsSupportList', orderId])
      const prev = queryClient.getQueryData<DSReceptionSupportDTO[]>(['dsSupportList', orderId])

      if (rowId != null) {
        setSavingRow((m) => ({ ...m, [rowId]: true }))
        queryClient.setQueryData<DSReceptionSupportDTO[]>(
          ['dsSupportList', orderId],
          (old) => (old || []).map(r => (r.id === rowId ? { ...r, supportNumber: payload.supportNumber } : r))
        )
      }

      return { prev, rowId }
    },

    onError: (err, _payload, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['dsSupportList', orderId], ctx.prev)
      showNotification({ message: `Nu s-a putut salva cantitatea. ${formatErrorMessage(err)}`, type: 'error' })
    },

    onSuccess: (_, __, ctx) => {
      showNotification({ message: 'Suport salvat cu succes', type: 'success' })
      if (ctx?.rowId != null) {
        setSavingRow(m => {
          const { [ctx.rowId!]: _, ...rest } = m
          return rest
        })
        setEditedQty(m => {
          const { [ctx.rowId!]: __, ...rest } = m
          return rest
        })
      } else {
        setIsAdding(false)
        setNewSupportType(null)
        setNewSupport({ dsSupportTypeId: null, supportNumber: 0 })
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries(['dsSupportList', orderId])
    },
  })

  const deleteMutation = useMutation(
    async (dsSupportTraceabilityId) => makeCoreDeleteRequest(`/shop-reception-mobile/ds-order/ds-support-traceability/delete-support/${orderId}/${supplierCode}/${dsSupportTraceabilityId}`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dsSupportList', orderId])
        showNotification({ message: 'Suport șters', type: 'success' })
      },
      onError: (err) =>
        showNotification({
          message: `Eroare la ștergerea suportului: ${formatErrorMessage(err)}`,
          type: 'error'
        }),
    }
  )

  const finalizeMutation = useMutation(
    async () => makeCorePostRequest(`/shop-reception-mobile/ds-order/ds-support-traceability/finalize/${orderId}/${supplierCode}`, null),
    {
      onSuccess: () => {
        navigate(generatePath(URLS.AUTH_SUPPLIERS_RECEIPT_REPORT, { id: orderId }) + `?supplierCode=${supplierCode}`)
      },
      onError: (err) =>
        showNotification({
          message: `Eroare la finalizarea recepției: ${formatErrorMessage(err)}`,
          type: 'error'
        }),
    }
  )

  const supportTypeMap = useMemo(() => {
    const map = new Map<number, string>()
    supportTypes?.forEach((s) => {
      map.set(s.id, `${s.name} (${s.code})`)
    })
    return map
  }, [supportTypes])

  const handleDelete = (dsSupportTraceabilityId) => {
    if (dsSupportTraceabilityId == null) return
    deleteMutation.mutate(dsSupportTraceabilityId)
  }

  const handleAddNew = () => {
    if (!newSupport.dsSupportTypeId) {
      showNotification({ message: 'Selectează un tip de ambalaj', type: 'warning' })
      return
    }
    if (newSupport.supportNumber < 0) {
      showNotification({ message: 'Cantitatea nu poate fi negativă', type: 'warning' })
      return
    }
    saveMutation.mutate({
      id: null,
      dsSupportTypeId: Number(newSupport.dsSupportTypeId),
      supportNumber: Number(newSupport.supportNumber) || 0,
    })
  }

  const handleQtyChange = (rowId: number | null, val: number) => {
    if (rowId == null) return
    setEditedQty(m => ({ ...m, [rowId]: val }))
  }

  const handleCommit = (row: DSReceptionSupportDTO) => {
    if (row.id == null) return
    const val = editedQty[row.id] ?? row.supportNumber
    saveMutation.mutate({
      id: row.id,
      dsSupportTypeId: row.dsSupportTypeId,
      supportNumber: Number(val),
    })
  }

  const supportTypeOptions = useMemo(
    () => supportTypes?.map(support => ({ value: support.id, label: support.name })) || [],
    [supportTypes]
  )

  if (isLoading) return <CircularProgress style={{ margin: '40px auto' }} />

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      itemsPerRow={1}
      items={[
        <TitleHeader title='SELECȚIE AMBALAJE' />,
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ background: '#0970e6' }}>
              <TableRow>
                <TableCell style={{ fontFamily: 'Ubuntu', color: '#fff', fontWeight: 'bold' }}>TIP AMBALAJ</TableCell>
                <TableCell align='center' style={{ fontFamily: 'Ubuntu', color: '#fff', fontWeight: 'bold' }}>CANTITATE</TableCell>
                <TableCell align='center' style={{ color: '#fff', fontWeight: 'bold' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supports?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" style={{ padding: '20px' }}>
                    Nu există ambalaje adăugate. Apasă "Adaugă ambalaj" pentru a începe.
                  </TableCell>
                </TableRow>
              )}
              {supports?.map((s) => (
                <TableRow key={s.id}>
                  <TableCell align="left">
                    {supportTypeMap.get(s.dsSupportTypeId) ?? '-'}
                  </TableCell>
                  <TableCell align='center'>
                    <div style={{ position: 'relative' }}>
                      <Input
                        type="number"
                        variant="standard"
                        value={s.id != null ? (editedQty[s.id] ?? s.supportNumber) : s.supportNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleQtyChange(s.id, Number(e.target.value || 0))
                        }
                        onBlur={() => handleCommit(s)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur() }}
                        disabled={s.id != null ? !!savingRow[s.id] : false}
                      />
                      {s.id != null && savingRow[s.id] && (
                        <span style={{ position: 'absolute', right: -24, top: 6, fontSize: 12 }}>⏳</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton onClick={() => handleDelete(s.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {isAdding && (
                <TableRow>
                  <TableCell>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="searchSupportType"
                      label="Cauta tip suport"
                      value={newSupportType}
                      options={supportTypeOptions}
                      onChange={(opt) => {
                        setNewSupportType(opt)
                        setNewSupport((prev) => ({ ...prev, dsSupportTypeId: opt ? Number(opt.value) : null, }))
                      }}
                      disabled={isLoadingTypes}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Input
                      type='number'
                      variant="standard"
                      placeholder='0'
                      value={newSupport.supportNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setNewSupport({ ...newSupport, supportNumber: Number(e.target.value) })
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Button
                      label='Salvează'
                      variant='contained'
                      onClick={handleAddNew}
                      disabled={saveMutation.isLoading}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>,
        !isAdding && (
          <Button
            type='button'
            variant='outlined'
            label='Adaugă ambalaj'
            onClick={() => setIsAdding(true)}
          />
        ),
        <Button
          type='button'
          variant='contained'
          label='Finalizează receptia'
          onClick={() => finalizeMutation.mutate()}
          disabled={finalizeMutation.isLoading}
          style={{ width: '100%' }}
        />
      ]}
    />
  )
}

export default PackageConfirmation
